import clsx, { ClassValue } from 'clsx';

export function debounce<T extends (...args: any[]) => any>(func: T, wait: number) {
  let timeout: ReturnType<typeof setTimeout>;

  return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const context = this;

    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
}

export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

/**
 * Formats a number of cents into a currency string using a Shopify format
 * string.
 *
 * https://help.shopify.com/en/manual/international/pricing/currency-formatting#currency-formatting-options
 *
 * Originally from: https://gist.github.com/stewartknapman/8d8733ea58d2314c373e94114472d44c
 *
 * @param cents - The number of cents to format.
 * @param formatString - The format string to use.
 * @returns The formatted currency string.
 */
export function shopifyFormatCurrency(cents: number, formatString: string) {
  const placeholderRegex = /{{\s*(\w+)\s*}}/;

  /**
   * Formats a number of cents into a currency string using the provided
   * precision, thousands separator, and decimal separator.
   * @param number - The number of cents to format.
   * @param precision - The number of decimal places to include.
   * @param thousands - The character to use as the thousands separator.
   * @param decimal - The character to use as the decimal separator.
   * @returns The formatted currency string.
   */
  function formatWithDelimiters(
    number: number,
    precision: number = 2,
    thousands: string = ',',
    decimal: string = '.',
  ) {
    if (isNaN(number) || number == null) {
      return '0';
    }

    const numString = number.toFixed(precision);
    const parts = numString.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const cents = parts[1] ? decimal + parts[1] : '';
    return dollars + cents;
  }

  return formatString.replace(placeholderRegex, (match, placeholder) => {
    switch (placeholder) {
      case 'amount':
        // Ex.	1,134.65
        return formatWithDelimiters(cents, 2);
      case 'amount_no_decimals':
        // Ex. 1,135
        return formatWithDelimiters(cents, 0);
      case 'amount_with_comma_separator':
        // Ex. 1.134,65
        return formatWithDelimiters(cents, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        // Ex. 1.135
        return formatWithDelimiters(cents, 0, '.', ',');
      case 'amount_with_apostrophe_separator':
        // Ex. 1'134.65
        return formatWithDelimiters(cents, 2, "'", '.');
      case 'amount_no_decimals_with_space_separator':
        // Ex. 1 135
        return formatWithDelimiters(cents, 0, ' ');
      case 'amount_with_space_separator':
        // 1 134,65
        return formatWithDelimiters(cents, 2, ' ', ',');
      case 'amount_with_period_and_space_separator':
        // 1 134.65
        return formatWithDelimiters(cents, 2, ' ', '.');
      default:
        return match;
    }
  });
}

/**
 * Currency Helpers
 * -----------------------------------------------------------------------------
 * A collection of useful functions that help with currency formatting
 *
 * Current contents
 * - formatMoney - Takes an amount in cents and returns it as a formatted dollar value.
 *
 */

const moneyFormat = '${{amount}}';

/**
 * Format money values based on your shop currency settings
 * @param  {Number|string} cents - value in cents or dollar amount e.g. 300 cents
 * or 3.00 dollars
 * @param  {String} format - shop money_format setting
 * @return {String} value - formatted value
 */
export function formatMoney(cents: number | string, format?: string): string {
  let value = '';
  let centsValue = cents;
  if (typeof centsValue === 'string') {
    centsValue = centsValue.replace('.', '');
  }

  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || moneyFormat;

  function formatWithDelimiters(
    number: number | string,
    precision = 2,
    thousands = ',',
    decimal = '.',
  ) {
    const num = Number(number);

    if (isNaN(num) || number == null) {
      return '0';
    }

    const fixed = (num / 100.0).toFixed(precision);

    const parts = fixed.split('.');
    const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const centsAmount = parts[1] ? decimal + parts[1] : '';

    return dollarsAmount + centsAmount;
  }

  const match = formatString.match(placeholderRegex);

  if (!match) {
    return formatString;
  }

  switch (match[1]) {
    case 'amount':
      value = formatWithDelimiters(centsValue, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(centsValue, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(centsValue, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(centsValue, 0, '.', ',');
      break;
  }

  return formatString.replace(placeholderRegex, value);
}

/**
 * Matches the first monetary amount in a Shopify `money_with_currency` string
 * (e.g. 10,95 or 1.234,56). Used to replace the sample amount without splitting
 * on comma decimals (which breaks European formats).
 */
export const FIRST_SHOPIFY_MONEY_AMOUNT_REGEX = /\d[\d.,]*/;

/**
 * Replaces the first amount in a Liquid `money_with_currency` sample (e.g. from
 * `{{ 1000 | money_with_currency }}`) with the value for `cents`, using
 * `Intl` and the storefront locale so separators match the currency.
 */
export function formatCentsInMoneyTemplate(template: string, cents: number): string {
  const amount = Number(cents) / 100;
  const locale =
    typeof window !== 'undefined' && (window as Window & { Shopify?: { locale?: string } }).Shopify?.locale
      ? (window as Window & { Shopify?: { locale?: string } }).Shopify!.locale!
      : 'en-AU';

  const isJpy =
    typeof window !== 'undefined' &&
    (window as Window & { Shopify?: { currency?: { active?: string } } }).Shopify?.currency
      ?.active === 'JPY';

  const formatter = Intl.NumberFormat(locale, {
    minimumFractionDigits: isJpy ? 0 : 2,
    maximumFractionDigits: isJpy ? 0 : 2,
  });
  const formatted = formatter.format(amount);
  const match = template.match(FIRST_SHOPIFY_MONEY_AMOUNT_REGEX);
  if (!match) return template;
  return template.replace(match[0], formatted);
}

  export function convertToShopCurrency(amount: number): number {
  const currencyRate = parseFloat(window.Shopify.currency.rate || '1');

    if (!currencyRate) {
      console.error('Could not find currency rate!');
      return amount;
    }

    const priceDollars = amount / 100;

    // Convert price to base currency - inflated currencies will have a rate less than 1 to scale back to base currency
    const baseCurrencyPrice =
      currencyRate < 1 ? priceDollars / currencyRate : priceDollars * currencyRate;

    console.log(`Raw price: $${priceDollars}`);
    console.log(`Base currency price: $${baseCurrencyPrice}`);
    
    return baseCurrencyPrice;
  }